import React, { useEffect } from 'react';
import {
  Grid,
  Typography,
  Theme,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import clsx from 'clsx';

import yup from '../../../utility/yup';
import { useActionDispatcher, useTitle } from '../../../hooks';
import { passwordRecoveryRequest } from '../../../store/modules/password-recovery/actions';
import { PasswordRecoveryPayload } from '../../../store/modules/password-recovery/types';
import {
  Error,
  FormInput,
  FormSubmit,
  Link,
  PagesTitle,
} from '../../../components';
import { push } from 'connected-react-router';
import {
  selectPasswordRecoveryForm,
  selectPasswordRecovery,
} from '../../../store/modules/password-recovery/selectors';
import { Links } from '../../../utility/links';
import PageContainer from '../PageContainer';

const useStyles = makeStyles(({ colors, palette }: Theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  form: {
    flexGrow: 1,
  },
  subtitle: {
    color: palette.text.primary,
    fontSize: '16px',
    marginTop: '16px',
  },
  control: {
    marginTop: '33px',
  },
  controlLabel: {
    color: palette.text.primary,
    fontSize: '14px',
    fontFamily: 'Roboto',
  },
  submit: {
    height: '40px',
    borderRadius: '5px',
    fontWeight: 600,
    fontFamily: 'Montserrat',
    color: palette.common.white,
    textTransform: 'none',
    width: '100%',
  },
  forgot: {
    display: 'block',
    fontSize: '14px',
    marginTop: '10px',
  },
}), { name: 'PasswordRecovery' });

const emailSchema = yup.object().shape({
  email: yup.string().email().required(),
});

const PasswordRecovery = () => {
  const classes = useStyles();

  useTitle('Forgot your password?');

  const dispatch = useDispatch();

  const handleRecovery = useActionDispatcher(passwordRecoveryRequest);

  const { loading, error } = useSelector(selectPasswordRecovery);

  const { email } = useSelector(selectPasswordRecoveryForm);

  useEffect(() => {
    if (!loading && error) {
      dispatch(push('/pages/password-recover-error'));
    } else if (!loading && email) {
      dispatch(push('/pages/password-recover-email-sent'));
    }
  }, [dispatch, loading, email, error]);

  const initialValues = {
    email: '',
  };

  return (
    <PageContainer>
      <Grid container className={classes.root}>
        <PagesTitle>Forgot your Password</PagesTitle>
        <Typography variant="subtitle1" className={classes.subtitle}>
          We&apos;ll help you reset it?
        </Typography>
        <Grid item xs={12} container>
          <Formik
            <PasswordRecoveryPayload>
            initialValues={initialValues}
            onSubmit={handleRecovery}
            validationSchema={emailSchema}
            validateOnMount
          >
            <Form className={classes.form}>
              <FormInput
                className={classes.control}
                fullWidth
                label="Email Address"
                name="email"
                autoComplete="email"
                dontCheckEmptyValue={false}
                dontCheckAfterSubmit
                autoFocus
              />
              <FormSubmit
                className={clsx(classes.submit, classes.control)}
                variant="contained"
                color="primary"
                disabled={loading}
              >
                Reset Password
              </FormSubmit>
            </Form>
          </Formik>
        </Grid>
        <Typography variant="subtitle1" className={classes.subtitle}>
          <Link to={Links.support} defaultAction target="_blank">
            Need Help?
          </Link>
        </Typography>
        <Error>{error}</Error>
      </Grid>
    </PageContainer>
  );
};

export default PasswordRecovery;
